<template>
  <CreateEditData 
        :model="'song'"
        :form="form"
        :similarData="similarSongs"
        :loading="loading"
        @fetchData="getSong">
    <template #headerActions>
        <div class="flex items-center gap-2">
            <div data-tour="song-ai-tools" v-if="hasAiFeature">
                <AIParsingUpload @parsed="handleAIParsed" :buttonText="$t('form.extractPdfImage', 'Extrair de PDF/Imagem')" :loadingText="$t('form.parsingAi', 'Analisando com IA...')" class="!m-0" />
            </div>
            <Tooltip :text="$t('form.delete')">
                <Button @click="confirmDelete" type="danger" :disabled="!songId" class="!m-0 !p-2 flex items-center justify-center">
                    <TrashIcon class="w-5 h-5" />
                </Button>
            </Tooltip>
            <Tooltip :text="$t('form.reset')">
                <Button @click="resetForm" type="secondary" class="!m-0 !p-2 flex items-center justify-center">
                    <ArrowPathIcon class="w-5 h-5 text-gray-500" />
                </Button>
            </Tooltip>
            <Tooltip v-if="songId" :text="$t('tooltip.createVersion', 'Create Version')">
                <Button @click="createVersion" type="secondary" class="!m-0 !p-2 flex items-center justify-center">
                    <ClockIcon class="w-5 h-5 text-gray-500" />
                </Button>
            </Tooltip>
            <Tooltip :text="$t('tour.startTour')">
                <Button @click="() => startSongEditorTour(true)" type="secondary" class="!m-0 !p-2 flex items-center justify-center text-indigo-500">
                    <QuestionMarkCircleIcon class="w-5 h-5" />
                </Button>
            </Tooltip>
            <Button data-tour="song-save-btn" @click="handleSubmit" type="clear" :disabled="isSaving || !canEdit || (songId && !isDirty)" class="!m-0">
                {{ isSaving ? $t('form.saving', 'Saving...') : $t('form.save') }}
            </Button>
        </div>
    </template>
    <form @submit.prevent="handleSubmit" class="">

                  <!-- Title & Artist -->
                  <div data-tour="song-info-fields" class="flex flex-col">
                      <FormContainer>
                          <Label :label="'Title *'" />
                          <Input
                              id="title"
                              v-model="form.title"
                              type="text"
                              required
                              placeholder="Enter song title"
                              @input="searchSimilarSongs"
                              :class="{ 'border-red-500 ring-red-500': showErrors && !form.title }"
                          />
                          <p v-if="showErrors && !form.title" class="text-xs text-red-500 mt-1">Title is required.</p>
                      </FormContainer>
                      <!-- Artist Autocomplete -->
                      <FormContainer class="relative z-30">
                          <Label :label="'Artist *'" />
                          <AutocompleteInput
                              v-model="form.artist"
                              :items="artists"
                              :allow-new="true"
                              placeholder="Search or add artist"
                              @add-new="addNewArtist"
                              @search="searchArtist"
                              required
                              :class="{ 'ring-2 ring-red-500 rounded-md': showErrors && !form.artist }"
                          />
                          <p v-if="showErrors && !form.artist" class="text-xs text-red-500 mt-1">Artist is required.</p>
                      </FormContainer>
                  </div>

                  <!-- Composers Autocomplete -->
                  <FormContainer class="relative z-20">
                      <Label :label="'Composers'" />
                      <AutocompleteInput
                          v-model="form.composer"
                          :items="composer"
                          :allow-new="true"
                          :multiple="true"
                          placeholder="Search or add composers"
                          @search="searchComposer"
                      />
                  </FormContainer>

                  <!-- BPM / Tempo -->
                  <FormContainer data-tour="song-bpm-tempo">
                      <Label :label="'BPM / Tempo'" />
                      <div class="flex items-center gap-3">
                          <Input
                              id="bpm"
                              v-model.number="form.bpm"
                              type="number"
                              min="30"
                              max="260"
                              placeholder="ex: 120"
                              class="max-w-[140px]"
                          />
                          <button
                              type="button"
                              @click="handleFormTapTempo"
                              class="px-3 py-2 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900 rounded-lg text-xs font-bold active:scale-95 transition-transform"
                          >
                              TAP TEMPO
                          </button>
                          <span v-if="form.bpm" class="text-xs text-gray-500 font-mono">
                              {{ form.bpm }} batidas por minuto
                          </span>
                      </div>
                  </FormContainer>

                  <!-- Lyrics -->
                  <FormContainer data-tour="song-editor-area">
                      <div class="flex flex-row gap-2 items-center">
                        <Label :label="'Content *'" />
                        <!-- <Tooltip text="Clean Song">
                          <button type="button" class="bg-white dark:bg-gray-800 rounded-full p-1 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 hover:shadow-md">
                            <BeakerIcon class="w-6 h-6 text-gray-500 cursor-pointer hover:text-gray-400" @click="cleanSong" />
                          </button>
                        </Tooltip> -->
                      </div>
                      <div class="flex flex-col md:flex-row gap-2 items-start w-full">
                        <SongEditor
                            :key="songId || 'new-song'"
                            id="lyric"
                            v-model="form.lyric"
                            style="--editor-toolbar-top: 38px;"
                            class="min-h-[500px] flex-1 w-full border rounded-md shadow-sm bg-white dark:bg-gray-900 music-map"
                            :class="showErrors && !form.lyric ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'"
                            :border="false"
                        />
                      </div>
                      <p v-if="showErrors && !form.lyric" class="text-xs text-red-500 mt-1">Song content is required.</p>
                  </FormContainer>

                  <!-- Media URLs -->
                  <MediaInput v-model="form.media" />

                  <!-- Tags -->
                  <FormContainer>
                      <div class="flex items-center gap-2">
                        <Label :label="'Tags'" class="mb-0" />
                        <AIGenerateButton 
                          :loading="isGeneratingTags" 
                          @click="generateTagsAI" 
                        />
                      </div>
                      <TagInput
                          v-model="form.tag"
                          :available-tags="tag"
                          :allow-new="true"
                          :items="tag"
                          placeholder="Add tags"
                          @search="searchTag"
                          
                      />
                  </FormContainer>

              </form>

    </CreateEditData>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue'
import AutocompleteInput from '@/components/AutocompleteInput.vue'
import TagInput from '@/components/TagInput.vue'
import SongEditor from '@/components/editor/SongEditor.vue'
//import List from '@/components/common/TriadList.vue'
import MediaInput from '@/components/form/MediaInput.vue'
import toastStore from '@/store/toast.store'
import {debounce} from '@/service/debounce'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ClockIcon, TrashIcon, ArrowPathIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'
import Tooltip from '@/components/common/elements/TriadTooltip.vue'
import AIParsingUpload from '@/components/common/AIParsingUpload.vue'
import songService from '@/service/song.service'
import userStore from '@/store/user.store'
import dialogStore from '@/store/dialog.store'
import AIGenerateButton from '@/components/common/elements/AIGenerateButton.vue'
import { useTour } from '@/composables/useTour'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { startSongEditorTour } = useTour()
const songId = computed(() => route.params.id)
const loading = ref(false)
const showErrors = ref(false)
const isGeneratingTags = ref(false)

const hasAiFeature = computed(() => userStore.getters.hasAiFeature);

watch(() => route.params.id, (newId) => {
  if (newId) {
    isDirty.value = false;
    getSong();
  } else {
    isDirty.value = true;
    resetForm();
  }
});

onMounted(() => {
  if (songId.value) {
    getSong()
  }
})


const getSong = async () => {
  if (!songId.value) return;
  loading.value = true;
  const response = await fetch(`/api/song/${songId.value}?format=html`)
  const data = await response.json();
  loading.value = false;
  form.title = data.title
  if (data.artist){
    form.artist = {id: data.artist._id, name: data.artist.name};
    artists.value.push(form.artist);
  }
  composer.value = [];
  if (data.composer) {
    data.composer.forEach((c, index) => {
      const composerName = typeof c === 'object' && c !== null ? c.name : c;
      composer.value.push({id: Date.now() + index, name: composerName})
    })
  }
  form.composer = composer.value;
  form.lyric = data.lyric;
  form.media = [];
  if (data.media) {
    data.media.forEach(m => {
      form.media.push(m)
    })
  }

  tag.value = [];
  if (data.tag) {
    data.tag.forEach((t, index) => {
      const tagName = typeof t === 'object' && t !== null ? t.name : t;
      tag.value.push({id: Date.now() + index + 1000, name: tagName})
    })
  }
  form.tag = tag.value;
  form.bpm = data.bpm || null;
  form.userId = data.userId || null;
  form.isPublic = data.isPublic || false;
  
  initialFormState = JSON.stringify({
    title: form.title,
    artist: form.artist ? form.artist.id || form.artist.name : null,
    composer: form.composer.map(c => c.name),
    bpm: form.bpm,
    lyric: form.lyric,
    media: form.media,
    tag: form.tag.map(t => t.name),
    isPublic: form.isPublic
  });
  console.log('getSong INITIAL STATE SET:', initialFormState);
  isDirty.value = false;
}

const form = reactive({
  preview: '',
  title: '',
  artist: null,
  composer: [],
  bpm: null,
  lyric: '',
  media: [],
  tag: [],
  userId: null,
  isPublic: false
})

let formTapTimes = []
const handleFormTapTempo = () => {
  const now = performance.now()
  if (formTapTimes.length > 0 && now - formTapTimes[formTapTimes.length - 1] > 2000) {
    formTapTimes = []
  }
  formTapTimes.push(now)
  if (formTapTimes.length > 4) {
    formTapTimes.shift()
  }
  if (formTapTimes.length >= 2) {
    let intervals = []
    for (let i = 1; i < formTapTimes.length; i++) {
      intervals.push(formTapTimes[i] - formTapTimes[i - 1])
    }
    const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length
    const calculated = Math.round(60000 / avg)
    if (calculated >= 30 && calculated <= 260) {
      form.bpm = calculated
    }
  }
}

const canEdit = computed(() => {
  if (!songId.value) return true;
  const userInfo = userStore.state.userInfo;
  const role = userInfo?.role;
  const isAdmin = Array.isArray(role) 
    ? (role.includes('ADM') || role.includes('ADMIN'))
    : (role === 'ADM' || role === 'ADMIN');
  const isOwner = !form.userId || (userInfo && (form.userId === userInfo.id || form.userId === userInfo._id));
  
  return isAdmin || isOwner;
})

let initialFormState = '';

const isDirty = ref(!songId.value);
const isSaving = ref(false);

watch(() => JSON.stringify({
  title: form.title,
  artist: form.artist ? form.artist.id || form.artist.name : null,
  composer: form.composer.map(c => c.name),
  bpm: form.bpm,
  lyric: form.lyric,
  media: form.media,
  tag: form.tag.map(t => t.name),
  isPublic: form.isPublic
}), (currentFormState) => {
  if (songId.value) {
    isDirty.value = currentFormState !== initialFormState;
  } else {
    isDirty.value = true;
  }
})

// const mainFormClass = computed(() => {
//   return {
//     'md:col-span-8': (similarSongs.value.length > 0),
//     'md:col-span-12': similarSongs.value.length === 0
//   }
// })

// const getTitle = computed(() => {
//   if (songId) {
//     return 'Update Song';
//   } else {
//     return 'Create New Song';
//   }
// })
// These would typically come from your API/store
const artists = ref([])
const composer = ref([])
const tag = ref([])
const similarSongs = ref([])

const addNewArtist = (name) => {
  // Here you would typically make an API call to create a new artist
  const newArtist = { id: Date.now(), name }
  artists.value.push(newArtist)
  form.artist = newArtist
}

// const addNewComposer = (name) => {
//   // Here you would typically make an API call to create a new composer
//   const newComposer = { id: Date.now(), name }
//   composer.value = composer.value.filter( c => c.name != newComposer.name);
//   composer.value.push(newComposer)
//   form.composer = form.composer.filter( c => c.name != newComposer.name);
//   form.composer.push(newComposer);
// }

const handleSubmit = async () => {
  if (isSaving.value) return;
  try {
    if (!isFormValid.value) {
      showErrors.value = true;
      toastStore.dispatch('addMessage', {
        message: 'Please fill in all required fields',
        icon: 'error',
        ttl: 3000
      });
      return;
    }
    showErrors.value = false;
    isSaving.value = true;
    const payload = {
      ...form,
      composer: form.composer.map(c => ({ name: c.name })),
      tag: form.tag.map(t => ({ name: t.name })),
      is_public: form.isPublic
    };
    let response;
    if (songId.value) {
      response = await fetch('/api/song/' + songId.value, {
        method: 'PUT',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } else {
      response = await fetch('/api/song/', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json'
        }
      });
    }
    
    if (response.status === 200) {
        toastStore.dispatch('addMessage', {
            message: 'Song saved',
            icon: 'success',
            ttl: 3000
        });
        if (songId.value) {
          initialFormState = JSON.stringify({
            title: form.title,
            artist: form.artist ? form.artist.id || form.artist.name : null,
            composer: form.composer.map(c => c.name),
            lyric: form.lyric,
            media: form.media,
            tag: form.tag.map(t => t.name),
            isPublic: form.isPublic
          });
          isDirty.value = false;
        } else {
          try {
            const createdSong = await response.json();
            const newSongId = createdSong?.id || createdSong?._id;
            if (newSongId) {
              router.push(`/song/${newSongId}`);
              return;
            }
          } catch (e) {
            // response body was already consumed or not json
          }
          resetForm();
        }
    } else {
        const errorData = await response.json().catch(() => ({}));
        let errMsg = 'Error saving song';
        if (response.status === 403 || errorData?.message === 'LIMIT_REACHED' || errorData?.error === 'LIMIT_REACHED') {
            errMsg = 'Song limit reached. Upgrade your plan to create more songs.';
        } else if (errorData?.message) {
            errMsg = errorData.message;
        }
        toastStore.dispatch('addMessage', {
            message: errMsg,
            icon: 'error',
            ttl: 3000
        });
    }
  } catch (error) {
    console.error('Error saving song:', error);
    toastStore.dispatch('addMessage', {
        message: 'Error saving song',
        icon: 'error',
        ttl: 3000
    });
  } finally {
    isSaving.value = false;
  }
};

const handleAIParsed = (data) => {
  if (data.title) form.title = data.title;
  if (data.lyric) form.lyric = data.lyric;
  if (data.artist) {
    form.artist = { id: Date.now(), name: data.artist };
    artists.value.push(form.artist);
  }
}

const generateTagsAI = async () => {
  if (!form.title && !form.lyric) {
    toastStore.dispatch('addMessage', {
      message: 'Title or lyrics needed for tag generation.',
      icon: 'warning',
      ttl: 3000
    });
    return;
  }
  
  isGeneratingTags.value = true;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000);

  try {
    const payload = {
      title: form.title,
      artist: form.artist?.name,
      composer: form.composer.map(c => c.name),
      lyric: form.lyric
    };
    
    const response = await fetch('/api/ai/generate-tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      if (data.tags && Array.isArray(data.tags)) {
        // add avoiding duplicates
        const currentTagNames = form.tag.map(t => t.name.toLowerCase());
        const newTags = data.tags.filter(t => !currentTagNames.includes(t.name.toLowerCase()));
        
        form.tag = [...form.tag, ...newTags];
        
        toastStore.dispatch('addMessage', {
          message: 'Tags generated successfully!',
          icon: 'success',
          ttl: 3000
        });
      }
    } else {
      throw new Error('Failed to generate tags');
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      toastStore.dispatch('addMessage', {
        message: 'Tag generation timed out after 1 minute.',
        icon: 'warning',
        ttl: 4000
      });
    } else {
      console.error('Error generating tags:', error);
      toastStore.dispatch('addMessage', {
        message: 'Failed to generate tags',
        icon: 'error',
        ttl: 3000
      });
    }
  } finally {
    isGeneratingTags.value = false;
  }
}

const isFormValid = computed(() => {
  return form.title && form.lyric && form.artist;
});

const confirmDelete = async () => {
    if (await dialogStore.dispatch('confirm', t('form.deleteConfirm'))) {
        try {
            const response = await fetch(`/api/song/${songId.value}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                toastStore.dispatch('addMessage', { message: t('toast.songDeleted'), icon: 'success' });
                router.push('/song');
            } else {
                toastStore.dispatch('addMessage', { message: t('toast.errorDeletingSong'), icon: 'error' });
            }
        } catch (error) {
            console.error('Error deleting song:', error);
            toastStore.dispatch('addMessage', { message: t('toast.errorDeletingSong'), icon: 'error' });
        }
    }
}

const resetForm = () => {
  if (songId.value){
    getSong()
  } else {
    form.title = ''
    form.artist = null
    form.composer = []
    form.lyric = ''
    form.media = ['']
    form.tag = [];
    form.isPublic = false;
    toastStore.dispatch('addMessage', {
      message: 'Form reseted',
      icon: 'success',
      ttl: 3000
    })
  }
}

const searchArtist = debounce(async (query) => {
  const response = await fetch(`/api/artist/search?q=${query}`);
  const data = await response.json()
  artists.value = data
}, 500)

const searchComposer = debounce(async (query) => {
  const response = await fetch(`/api/composers/search?q=${query}`);
  const data = await response.json()
  composer.value = data
}, 500)

const searchTag = debounce(async (query) => {
  const response = await fetch(`/api/tags/search?q=${query}`);
  const data = await response.json()
  tag.value = data
}, 500)

const searchSimilarSongs = debounce(async (ev) => {
  console.log(ev)
  console.log("searchSimilarSongs disabled")
  return
  // if(songId) {
  //   return;
  // }
  // toastStore.dispatch('addMessage', {
  //     message: 'Searching for similar songs',
  //     icon: 'info',
  //     ttl: 1000
  // })
  // const response = await fetch(`/api/song/search?q=${ev.target.value.replace(/[\r\n]+/g, ' ')}`);
  // if (response.status === 200) {
  //   const data = await response.json()
  //   similarSongs.value = data.map( (s) => {
  //       return {
  //           ...s,
  //           name: s.title,
  //           extra: (s.artist ? s.artist.name: "Unknown Artist") + " - " + s.compatibility
  //       }
  //   })
  //   if (similarSongs.value.length > 0) {
  //       toastStore.dispatch('addMessage', {
  //           message: similarSongs.value.length + ' similar songs found',
  //           icon: 'success',
  //           ttl: 5000
  //       })
  //   }
  // } else {
  //   similarSongs.value = [];
  //   toastStore.dispatch('addMessage', {
  //     message: 'Error searching for similar songs',
  //     icon: 'error',
  //     ttl: 5000
  //   })
  // }
}, 1000)

// const openSong = (song) => {
//   songPreviewStore.dispatch("open", song._id)
// }


// const cleanSong = async () => {
//   const response = await fetch('/api/song/clean', {
//     method: 'POST',
//     body: JSON.stringify({ lyric: form.lyric }),
//     headers: {
//       'Content-Type': 'application/json'
//     }
//   })
//   const data = await response.json()
//   form.lyric = data.lyric
//   toastStore.dispatch('addMessage', {
//     message: 'Song cleaned',
//     icon: 'success',
//     ttl: 3000
//   })
// }
const createVersion = async () => {
    if (!songId.value) {
        toastStore.dispatch('addMessage', {message: 'Cannot create version for song without ID', icon: 'error'})
        return
    }
    
    const now = new Date()
    const versionName = `Version ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`
    
    try {
        const response = await songService.createSongVersion(songId.value, {
            name: versionName,
            content: form.lyric,
            chord: null,
            media: [],
            is_public: false
        })
        
        if (response.ok) {
            toastStore.dispatch('addMessage', {message: `Version "${versionName}" created successfully`, icon: 'success'})
        } else {
            const errorData = await response.json()
            toastStore.dispatch('addMessage', {message: errorData.message || 'Error creating version', icon: 'error'})
        }
    } catch (error) {
        console.error('Error creating version:', error)
        toastStore.dispatch('addMessage', {message: 'Error creating version', icon: 'error'})
    }
}


</script>

